import { isChatError } from "@lazarentertainment/public-chat-web"
import { usePublicChatContext } from "@lazarentertainment/public-chat-web/dist/src/react"
import { ArrowUp } from "@lazarentertainment/web-design-system/components/iconOutline"
import { Button } from "client/designSystem/Button"
import emoji from "node-emoji"
import { MutableRefObject, useEffect } from "react"
import { useComponentId } from "shared/hooks/useComponentId"
import { usePreviousValue } from "shared/hooks/usePreviousValue"
import { checkText } from "smile2emoji"
import styled from "styled-components"

import { ChatView } from "../constants"

import { useChatContext } from "./ChatContext"
import { ChatInputWrapper, ChatTextInput } from "./ChatTextInput"

const Form = styled.form<{ $isMobileView: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  padding: 0;
  > :not(:last-child) {
    margin-right: ${(props) =>
      props.$isMobileView
        ? props.theme.mobile.buttonMargin
        : props.theme.main.buttonMargin};
  }
`

interface ChatInputProps {
  inputRef: MutableRefObject<HTMLInputElement>
  autoFocus?: boolean
  uiView: ChatView
}

export const ChatInput = ({
  inputRef,
  autoFocus,
  uiView,
}: ChatInputProps) => {
  const {
    messageInputValue,
    setMessageInputValue,
    isMobileView,
    chatError,
    setIsJoinModalOpen,
    isJoinModalOpen,
    writeReady,
  } = useChatContext()

  // This enables us to check whether writeReady CHANGES from false to true
  const previousWriteReady = usePreviousValue(writeReady)

  const { pluginState, displayName, submitMessage } =
    usePublicChatContext()
  const componentId = useComponentId()
  const formId = `chat-input-form-${componentId}`
  const onSubmit = () => {
    if (pluginState !== "enabled") {
      return
    }

    if (!displayName) {
      setIsJoinModalOpen(true)
      return
    }

    if (!messageInputValue.trim().length) {
      return
    }

    // Substitute emoticons and shorthands for emojis
    const parsedInputValue = emoji.emojify(checkText(messageInputValue))

    try {
      // submitMessage may throw synchronously with, for example,
      // "QuotaLimitReachedError", so we only clear the message input
      // if we successfully submit the message.
      submitMessage(parsedInputValue)
      setMessageInputValue("")
    } catch (error: unknown) {
      if (isChatError(error)) {
        if (error.code === "DisplayNameRejectedError") {
          setIsJoinModalOpen(true)
        }
        // Otherwise ignore, this gets handled elsewhere
        return
      }

      throw error
    }
    inputRef.current?.focus()
  }

  // Submit message when the user successfully 'signs up'
  // i.e. when they set their user name after attempting to submit their first message
  useEffect(() => {
    if (isJoinModalOpen && previousWriteReady === false && writeReady) {
      onSubmit()
    }
  }, [isJoinModalOpen, writeReady, previousWriteReady])

  useEffect(() => {
    if (!inputRef.current) {
      return
    }

    // Profane messages are "shadow blocked". They get added to the local
    // user's state, but are not published to other chat users, and the
    // local user is not notified.
    if (chatError && chatError.code !== "ProfaneMessageError") {
      inputRef.current.blur()
    } else {
      inputRef.current.focus()
      // Move cursor to end of input
      inputRef.current.setSelectionRange(
        inputRef.current.value.length,
        inputRef.current.value.length,
      )
    }
  }, [chatError, inputRef.current])

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
      $isMobileView={isMobileView}
      id={formId}
    >
      <ChatInputWrapper $uiView={uiView}>
        <ChatTextInput
          ref={inputRef}
          autoComplete="off"
          maxLength={200}
          id="message-input"
          placeholder="Say something..."
          value={messageInputValue}
          onChange={(event) => setMessageInputValue(event.target.value)}
          $uiView={uiView}
          disabled={pluginState !== "enabled"}
          autoFocus={autoFocus}
        />
      </ChatInputWrapper>
      <Button
        variant="primary"
        size="md"
        icon={ArrowUp}
        iconOnly
        disabled={
          pluginState !== "enabled" ||
          messageInputValue.trim().length === 0
        }
        form={formId}
        type="submit"
        data-robot="submit-chat-button"
      />
    </Form>
  )
}
